// Theme handling.
//
// The app has shipped a complete set of `.dark` tokens since the beginning with
// nothing to apply the class, so dark mode was unreachable dead code. This makes
// it reachable and remembers the choice.
//
// Default is "system": an admin tool that ignores the OS preference and blasts
// white at someone working at night is a small daily annoyance, and respecting
// it costs one media query.

import { createContext, useContext, useEffect, useState } from "react";

export type Theme = "light" | "dark" | "system";

const KEY = "bloxx_theme";

function systemPrefersDark() {
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false;
}

function apply(theme: Theme) {
  const dark = theme === "dark" || (theme === "system" && systemPrefersDark());
  document.documentElement.classList.toggle("dark", dark);
}

const Ctx = createContext<{ theme: Theme; setTheme: (t: Theme) => void; isDark: boolean }>({
  theme: "system",
  setTheme: () => {},
  isDark: false,
});

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(
    () => (localStorage.getItem(KEY) as Theme) || "system"
  );
  // Resolved state, so the toggle can be a plain light/dark switch. Cycling
  // through three states made users click twice to reach the one they wanted
  // and gave no clue which state they were in.
  const [isDark, setIsDark] = useState(
    () => theme === "dark" || (theme === "system" && systemPrefersDark())
  );

  useEffect(() => {
    apply(theme);
    setIsDark(theme === "dark" || (theme === "system" && systemPrefersDark()));
    // Track OS changes only while following the system, so an explicit choice
    // is never silently overridden.
    if (theme !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => { apply("system"); setIsDark(systemPrefersDark()); };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [theme]);

  const setTheme = (t: Theme) => {
    localStorage.setItem(KEY, t);
    setThemeState(t);
  };

  return <Ctx.Provider value={{ theme, setTheme, isDark }}>{children}</Ctx.Provider>;
}

export function useTheme() {
  return useContext(Ctx);
}
